import type { DragOverEvent } from '@dnd-kit/react';
import type { DashboardSectionItemData } from './DashboardSectionItem';

import { DragDropProvider, useDroppable } from '@dnd-kit/react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import Button from '../../../../components/Button/Button';

import { CloseIcon } from '../../../../images/shapes';

import DashboardSectionItem from './DashboardSectionItem';

export interface DashboardCustomizationSidebarProps {
  /**
   * Sections currently visible on the dashboard.
   */
  activeSections: DashboardSectionItemData[];
  /**
   * Hidden sections that can be added to the dashboard.
   */
  availableSections: DashboardSectionItemData[];
  /**
   * Whether there are unsaved dashboard changes.
   */
  dirty: boolean;
  /**
   * Whether the sidebar is open.
   */
  isOpen: boolean;
  /**
   * Callback fired when a section is dragged over another group.
   */
  onDragOver: (event: DragOverEvent) => void;
  /**
   * Callback closing the sidebar.
   */
  onClose: () => void;
  /**
   * Callback saving the dashboard changes.
   */
  onSave: () => void;
}

const DashboardCustomizationSections = ({
  activeSections,
  availableSections,
  activeTitle,
  availableTitle,
}: {
  activeSections: DashboardSectionItemData[];
  availableSections: DashboardSectionItemData[];
  activeTitle: string;
  availableTitle: string;
}) => {
  const { ref: active } = useDroppable({ id: 'active' });
  const { ref: available } = useDroppable({ id: 'available' });

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
      <hr className="border-slate-300 dark:border-slate-800" />

      <section className="flex min-h-12 flex-col gap-2" ref={active}>
        <h3 className="text-lg font-semibold capitalize text-slate-500 dark:text-slate-400">
          {activeTitle}
        </h3>
        {activeSections.map(({ id, index, label, onClick }) => (
          <DashboardSectionItem
            key={id}
            id={id}
            index={index}
            label={label}
            variant="active"
            onClick={onClick}
          />
        ))}
      </section>
      <hr className="border-slate-300 dark:border-slate-800" />
      <section className="flex min-h-12 flex-col gap-2" ref={available}>
        <h3 className="text-lg font-semibold capitalize text-slate-500 dark:text-slate-400">
          {availableTitle}
        </h3>
        {availableSections.map(({ id, index, label, onClick }) => (
          <DashboardSectionItem
            key={id}
            id={id}
            index={index}
            label={label}
            variant="available"
            onClick={onClick}
          />
        ))}
      </section>
    </div>
  );
};

const DashboardCustomizationSidebar = ({
  activeSections,
  availableSections,
  dirty,
  isOpen,
  onDragOver,
  onClose,
  onSave,
}: DashboardCustomizationSidebarProps) => {
  const { t } = useTranslation();

  return createPortal(
    <div
      className={`fixed top-0 z-39 w-60 h-full px-4 py-6
        transition-transform duration-100 bg-white ease-in-out motion-reduce:transition-none
        dark:bg-slate-950
        ${isOpen ? 'transform-none' : '-translate-x-full pointer-events-none'}`}
      aria-hidden={!isOpen}
      inert={!isOpen}
    >
      <div className="flex h-full flex-col gap-6">
        <div>
          <Button
            buttonColor="borderless"
            iconImage={<CloseIcon className="w-4 h-4" />}
            additionalClasses="ml-auto mb-4"
            onClick={onClose}
            aria-label={t('Home.DashboardCustomization.Close')}
          />
          <div>
            <h2 className="text-3xl font-semibold text-slate-900 dark:text-slate-200">
              {t('Home.DashboardCustomization.Title')}
            </h2>
            <p className="mt-1 text-base text-slate-500 ">
              {t('Home.DashboardCustomization.Description')}
            </p>
          </div>
        </div>

        <div className="flex gap-2">
          <Button
            buttonColor="blueBordered"
            additionalClasses="flex-1"
            onClick={onClose}
          >
            {t('Global.Cancel')}
          </Button>
          <Button
            buttonColor="blue"
            additionalClasses="flex-1"
            disabled={!dirty}
            onClick={onSave}
          >
            {t('Global.SaveChanges')}
          </Button>
        </div>

        <DragDropProvider onDragOver={onDragOver}>
          <DashboardCustomizationSections
            activeSections={activeSections}
            availableSections={availableSections}
            activeTitle={t('Home.DashboardCustomization.Active', {
              count: activeSections.length,
            })}
            availableTitle={t('Home.DashboardCustomization.Available', {
              count: availableSections.length,
            })}
          />
        </DragDropProvider>
      </div>
    </div>,
    document.body,
  );
};

export default DashboardCustomizationSidebar;
